<template>
  <div class="refund-step-amount">
    <div class="step-description">
      <p>{{ t('Informe o valor a ser estornado e o motivo') }}</p>
    </div>

    <!-- Original Amount Info -->
    <div class="original-amount-info">
      <div class="info-row">
        <span class="info-label">{{ t('Valor original do recebimento') }}:</span>
        <span class="info-value">{{ formatCurrency(originalAmountDisplay) }}</span>
      </div>
      <div class="info-row" v-if="recebimento.recebimento_id_externo">
        <span class="info-label">{{ t('ID da cobrança') }}:</span>
        <span class="info-value code">{{ recebimento.recebimento_id_externo }}</span>
      </div>
    </div>

    <!-- Refund Amount Input -->
    <div class="amount-input-container">
      <input-currency
        data-testid="recebimento-refund-amount"
        :label="t('Valor do estorno')"
        :placeholder="t('0,00')"
        :validation="() => isValid"
        :required="true"
        elem-id="refund_amount"
        v-model="amountDisplay">
      </input-currency>

      <!-- Quick Actions -->
      <div class="quick-actions">
        <Button
          data-testid="recebimento-refund-full-amount-button"
          :label="t('Estorno total')"
          icon="pi pi-wallet"
          size="small"
          severity="secondary"
          outlined
          @click="setFullAmount"
        />
      </div>
    </div>

    <!-- Validation Messages -->
    <div v-if="validationError" class="validation-error">
      <i class="pi pi-exclamation-triangle"></i>
      <span>{{ validationError }}</span>
    </div>

    <!-- Refund Preview -->
    <div class="refund-preview" v-if="amountDisplay > 0 && isValid">
      <span class="preview-label">{{ t('Valor a estornar') }}:</span>
      <span class="preview-value">{{ formatCurrency(amountDisplay) }}</span>
    </div>

    <!-- Reason Input (Required) -->
    <div class="reason-input-container">
      <label for="refund_reason" class="reason-label">{{ t('Motivo do estorno') }} <span class="required">*</span></label>
      <input-rich-text
        data-testid="recebimento-refund-reason"
        elem-id="refund_reason"
        v-model="reasonText"
        :placeholder="t('Ex: Cliente solicitou cancelamento do serviço')"
        :required="true"
        :validation="() => reasonText.trim().length > 0">
      </input-rich-text>
      <small v-if="!reasonText.trim()" class="p-error">{{ t('O motivo do estorno é obrigatório') }}</small>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { permissionsState } from '@/services/permissionsService';
import { currencyMinorUnitsFactor, LOCALE_TO_CURRENCY } from '@/utils/currencyMinorUnits';

interface RefundModel {
  amount?: number;
  description?: string;
  [key: string]: unknown;
}

interface RecebimentoSource {
  recebimento_valor?: number;
  recebimento_id_externo?: string;
  [key: string]: unknown;
}

const { t } = useI18n();

const props = defineProps<{
  modelValue: RefundModel;
  recebimento: RecebimentoSource;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: RefundModel];
}>();

// Price factor derived from active locale (100 for BRL, 1 for CLP, etc.)
const priceFactor = currencyMinorUnitsFactor((permissionsState as { normalizedLocale?: string })?.normalizedLocale);


// Original amount in display format (reais)
const originalAmountDisplay = computed<number>(() => {
  return (props.recebimento.recebimento_valor || 0) / priceFactor;
});

// Refund amount display (converts between cents and display value)
const amountDisplay = computed<number>({
  get: () => {
    return (props.modelValue.amount || 0) / priceFactor;
  },
  set: (value: number) => {
    emit('update:modelValue', {
      ...props.modelValue,
      amount: Math.round(value * priceFactor)
    });
  }
});

// Reason text
const reasonText = computed<string>({
  get: () => props.modelValue.description || '',
  set: (value: string) => {
    emit('update:modelValue', {
      ...props.modelValue,
      description: value
    });
  }
});

// Validation
const validationError = computed<string | null>(() => {
  const amount = props.modelValue.amount || 0;
  const originalAmount = props.recebimento.recebimento_valor || 0;

  if (amount <= 0) {
    return t('O valor do estorno deve ser maior que zero');
  }
  if (amount > originalAmount) {
    return t('O valor do estorno nao pode ser maior que o valor original');
  }
  return null;
});

const isValid = computed<boolean>(() => {
  const hasValidAmount = validationError.value === null && (props.modelValue.amount || 0) > 0;
  const hasReason = (props.modelValue.description || '').trim().length > 0;
  return hasValidAmount && hasReason;
});

// Methods
const setFullAmount = (): void => {
  emit('update:modelValue', {
    ...props.modelValue,
    amount: props.recebimento.recebimento_valor || 0
  });
};

const formatCurrency = (value: number | undefined): string => {
  const locale = (permissionsState as { normalizedLocale?: string })?.normalizedLocale || 'pt-BR';
  const currency = LOCALE_TO_CURRENCY[locale] || 'BRL';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency
  }).format(value || 0);
};

// Expose validation for wizard
defineExpose({
  isValid
});
</script>

<style scoped>
.refund-step-amount {
  padding: 1rem 0;
}

.step-description {
  margin-bottom: 1.5rem;
  color: var(--text-color-secondary);
}

.step-description p {
  margin: 0;
}

.original-amount-info {
  background-color: var(--surface-ground);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-left: 4px solid var(--primary-color);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0;
}

.info-row:not(:last-child) {
  margin-bottom: 0.5rem;
}

.info-label {
  color: var(--text-color-secondary);
  font-weight: 500;
}

.info-value {
  font-weight: 600;
  color: var(--text-color);
}

.info-value.code {
  font-family: monospace;
  font-size: 0.875rem;
  background-color: var(--surface-200);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.amount-input-container {
  max-width: 300px;
  margin-bottom: 1rem;
}

.quick-actions {
  margin-top: 0.75rem;
}

.validation-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--red-500);
  font-size: 0.875rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: var(--red-50);
  border-radius: 6px;
}

.validation-error i {
  flex-shrink: 0;
}

.refund-preview {
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: var(--yellow-50);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-left: 4px solid var(--yellow-500);
}

.preview-label {
  color: var(--text-color-secondary);
  font-weight: 500;
}

.preview-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--yellow-700);
}

.reason-input-container {
  margin-top: 1.5rem;
}

.reason-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-color);
}

.reason-textarea {
  width: 100%;
}

.required {
  color: var(--red-500);
  font-weight: bold;
}

.p-error {
  color: var(--red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>
